import Information16 from '@carbon/icons-react/es/information/16';
import { Tabs } from 'antd';
import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { useTranslation } from 'hooks/use-translation';
import { MentionType, Suggestion } from 'vendor/ckeditor/plugins/mention/types';
import { MentionData } from '..';
import { MembersSuggestions } from '../members-suggestion';
import { useMentions } from '../mention-store';
import { SpaceTabsSuggestions } from '../space-tabs-suggestion';
import { TasksSuggestions } from '../tasks-suggestion';
import styles from './mention-popup-content.module.css';

const { TabPane } = Tabs;

interface Props {
  query: string;
  activeTab?: MentionType;
  data?: MentionData;
  onTabChange: (tab: string) => void;
  onSelect: (args) => void;
}

export enum MentionsTabLabels {
  Users = 'mention.people',
  Tasks = 'mention.tasks',
  Documents = 'mention.documents',
}

export interface SuggestionComponentProps {
  onSelect: (selected: Suggestion) => void;
  query: string;
  activeTab: MentionType | undefined;
}

// TODO: Add better handling @asimkt
const renderFallback = () => (
  <p>
    <FormattedMessage id="loading" />
  </p>
);

export const MentionPopupContent: FC<Props> = ({
  activeTab,
  onTabChange,
  onSelect,
  query,
  data,
}) => {
  const peopleLabel = useTranslation(MentionsTabLabels.Users);
  const taskLabel = useTranslation(MentionsTabLabels.Tasks);
  const documentLabel = useTranslation(MentionsTabLabels.Documents);

  const userTabTitle = data?.members?.title || peopleLabel;
  const taskTabTitle = data?.tasks?.title || taskLabel;
  const documentTabTitle = data?.spaceTabs?.title || documentLabel;

  const mentionInfo = useTranslation('mention.info', {
    people: userTabTitle,
    tasks: taskTabTitle,
    documents: documentTabTitle,
  });

  const mentionRef = useRef<HTMLDivElement>(null);

  const [contextState] = useMentions();

  const { activeSuggestion } = contextState;

  useEffect(() => {
    if (mentionRef.current) {
      const activeMention = mentionRef.current.querySelector(
        '.mention__item--active',
      );

      activeMention?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [activeSuggestion]);

  return (
    <ErrorBoundary fallback={renderFallback}>
      <div ref={mentionRef}>
        <Tabs
          defaultActiveKey={activeTab}
          onChange={onTabChange}
          className={styles.wrapper}
          activeKey={activeTab}
        >
          <TabPane
            tab={userTabTitle}
            key={MentionType.User}
            className={styles.content_wrapper}
          >
            <MembersSuggestions
              data={data?.members?.items}
              onSelect={onSelect}
              query={query}
              activeTab={activeTab}
              isLoading={data?.members?.isLoading}
            />
          </TabPane>
          <TabPane
            tab={taskTabTitle}
            key={MentionType.Task}
            className={styles.content_wrapper}
          >
            <TasksSuggestions
              data={data?.tasks?.items}
              onSelect={onSelect}
              query={query}
              activeTab={activeTab}
              isLoading={data?.tasks?.isLoading}
            />
          </TabPane>
          <TabPane
            tab={documentTabTitle}
            key={MentionType.SpaceTab}
            className={styles.content_wrapper}
          >
            <SpaceTabsSuggestions
              data={data?.spaceTabs?.items}
              onSelect={onSelect}
              query={query}
              activeTab={activeTab}
              isLoading={data?.spaceTabs?.isLoading}
            />
          </TabPane>
        </Tabs>
        <div className={styles.mention_info}>
          <Information16 /> <p>{mentionInfo}</p>
        </div>
      </div>
    </ErrorBoundary>
  );
};
